// Group shift create + manage (Build P2d). Two steps:
//   1. Shift skeleton (home, service type, date/time, default ratio, travel, optional carer)
//   2. Select Clients (funded filter + per-row fund/ratio + Auto-Ratio) → per-participant billing panel
// Nothing about NDIS money lives here — the backend money engine (calculateGroupShift) owns it;
// this is the coordinator surface. Cyan accent to match group homes.
import React, { useState, useEffect, useCallback } from 'react';
import { X, Loader2, Users, Plus, Trash2, RefreshCw, Check, DollarSign, AlertTriangle } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useShiftTypes } from '../../hooks/useSettings';

const money = (n) => n == null ? '—' : `$${Number(n).toFixed(2)}`;
const fmtRatio = (num, den) => `1:${den || 1}`;

export default function GroupShiftModal({ shiftId: existingId, groupHomes = [], onClose, onSaved }) {
  const shiftTypes = useShiftTypes();
  const [shiftId, setShiftId] = useState(existingId || null);
  const [step, setStep] = useState(existingId ? 2 : 1);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    groupHomeId: '', shiftType: 'Community Access', shiftDate: '', startTime: '09:00', endTime: '15:00',
    breakMinutes: 0, defaultRatioDen: 3, apportionGroupTravel: true, staffId: '',
  });
  const [staff, setStaff] = useState([]);
  const [drawer, setDrawer] = useState(false);
  const [eligible, setEligible] = useState([]);
  const [picked, setPicked] = useState({});       // clientId → {fundId, ratioDen, checked}
  const [autoRatio, setAutoRatio] = useState(true);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get('/staff').then(r => setStaff((Array.isArray(r.data) ? r.data : r.data.staff || []).filter(s => ['carer', 'support_worker'].includes(s.role)))).catch(() => {}); }, []);

  const loadBilling = useCallback(async (id) => {
    try { const { data } = await api.get(`/groups/group-shifts/${id}/billing`); setBilling(data); }
    catch { toast.error('Failed to load billing'); }
  }, []);
  useEffect(() => { if (existingId) loadBilling(existingId); }, [existingId, loadBilling]);

  const createSkeleton = async () => {
    if (!form.shiftDate) return toast.error('Pick a date');
    setSaving(true);
    try {
      const { data } = await api.post('/groups/group-shifts', form);
      setShiftId(data.shift.id); setStep(2);
      toast.success('Group shift created — now add participants');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to create'); }
    finally { setSaving(false); }
  };

  const openDrawer = async () => {
    setDrawer(true); setLoading(true);
    try {
      const { data } = await api.get(`/groups/group-shifts/${shiftId}/eligible-clients`);
      setEligible(data.clients || []);
      // pre-tick residents that are funded
      const init = {};
      (data.clients || []).forEach(c => { if (c.is_resident && c.funded) init[c.id] = { fundId: c.fund_id, ratioDen: form.defaultRatioDen, checked: true }; });
      setPicked(init);
    } catch { toast.error('Failed to load clients'); }
    finally { setLoading(false); }
  };

  const togglePick = (c) => setPicked(p => {
    const next = { ...p };
    if (next[c.id]?.checked) next[c.id] = { ...next[c.id], checked: false };
    else next[c.id] = { fundId: c.fund_id, ratioDen: form.defaultRatioDen, checked: true };
    return next;
  });

  const addSelected = async () => {
    const clients = Object.entries(picked).filter(([, v]) => v.checked).map(([clientId, v]) => ({ clientId, fundId: v.fundId, ratioDen: autoRatio ? undefined : v.ratioDen }));
    if (!clients.length) return toast.error('Select at least one participant');
    setSaving(true);
    try {
      const { data } = await api.post(`/groups/group-shifts/${shiftId}/clients`, { clients, autoRatio });
      setBilling(data); setDrawer(false);
      toast.success(`${clients.length} participant${clients.length === 1 ? '' : 's'} added`);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to add'); }
    finally { setSaving(false); }
  };

  const removeParticipant = async (clientId) => {
    try { const { data } = await api.delete(`/groups/group-shifts/${shiftId}/clients/${clientId}`); setBilling(data); }
    catch { toast.error('Failed to remove'); }
  };
  const recalc = async () => {
    try { const { data } = await api.post(`/groups/group-shifts/${shiftId}/recalc`); setBilling(data); toast.success('Recalculated'); }
    catch { toast.error('Failed to recalc'); }
  };

  const checkedCount = Object.values(picked).filter(v => v.checked).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
          <div className="w-8 h-8 rounded-lg bg-cyan-600 flex items-center justify-center shrink-0"><Users size={15} className="text-white" /></div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-800">{existingId ? 'Manage Group Shift' : 'New Group Shift'}</h3>
            <p className="text-xs text-slate-400">{step === 1 ? 'Set up the group session' : 'Participants are billed per-ratio against their own funds'}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400"><X size={16} /></button>
        </div>

        <div className="overflow-y-auto flex-1 p-6">
          {/* Step 1 — skeleton */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Group Home (optional)">
                  <select className="input-field" value={form.groupHomeId} onChange={e => setForm(f => ({ ...f, groupHomeId: e.target.value }))}>
                    <option value="">— Ad-hoc group —</option>
                    {groupHomes.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                  </select>
                </Field>
                <Field label="Service Type">
                  <select className="input-field" value={form.shiftType} onChange={e => setForm(f => ({ ...f, shiftType: e.target.value }))}>
                    {(shiftTypes || []).filter(t => t.is_active !== false).map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                    {!(shiftTypes || []).some(t => t.name === 'Community Access') && <option value="Community Access">Community Access</option>}
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Field label="Date"><input type="date" className="input-field" value={form.shiftDate} onChange={e => setForm(f => ({ ...f, shiftDate: e.target.value }))} /></Field>
                <Field label="Start"><input type="time" className="input-field" value={form.startTime} onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))} /></Field>
                <Field label="End"><input type="time" className="input-field" value={form.endTime} onChange={e => setForm(f => ({ ...f, endTime: e.target.value }))} /></Field>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Field label="Default Ratio (1:N)" hint="Auto-set to participant count if Auto-Ratio is on">
                  <select className="input-field" value={form.defaultRatioDen} onChange={e => setForm(f => ({ ...f, defaultRatioDen: parseInt(e.target.value, 10) }))}>
                    {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>1:{n}</option>)}
                  </select>
                </Field>
                <Field label="Break (min)"><input type="number" min="0" className="input-field" value={form.breakMinutes} onChange={e => setForm(f => ({ ...f, breakMinutes: parseInt(e.target.value, 10) || 0 }))} /></Field>
                <Field label="Carer (optional)">
                  <select className="input-field" value={form.staffId} onChange={e => setForm(f => ({ ...f, staffId: e.target.value }))}>
                    <option value="">— Unassigned —</option>
                    {staff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
                  </select>
                </Field>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={form.apportionGroupTravel} onChange={e => setForm(f => ({ ...f, apportionGroupTravel: e.target.checked }))} className="rounded accent-cyan-600" />
                Apportion group travel equally across participants
              </label>
            </div>
          )}

          {/* Step 2 — participants + billing */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-slate-800">Participants</h4>
                <div className="flex gap-2">
                  <button onClick={recalc} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-600 hover:bg-slate-50"><RefreshCw size={12} /> Recalc</button>
                  <button onClick={openDrawer} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-600 text-white text-xs font-semibold hover:bg-cyan-700"><Plus size={12} /> Select Clients</button>
                </div>
              </div>

              {!billing || billing.billing?.length === 0 ? (
                <div className="text-center py-10 text-slate-400 text-sm border border-dashed border-slate-200 rounded-xl">No participants yet — click <b>Select Clients</b>.</div>
              ) : (
                <div className="card overflow-hidden border border-slate-200 rounded-xl">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-slate-50 text-xs font-medium text-slate-500">
                      <th className="text-left px-3 py-2">Participant</th><th className="text-left px-3 py-2">Fund</th>
                      <th className="text-center px-3 py-2">Ratio</th><th className="text-center px-3 py-2">Hrs</th>
                      <th className="text-right px-3 py-2">Rate</th><th className="text-right px-3 py-2">Line</th><th className="px-2" />
                    </tr></thead>
                    <tbody className="divide-y divide-slate-100">
                      {billing.billing.map(b => (
                        <tr key={b.id}>
                          <td className="px-3 py-2 font-medium text-slate-800">{b.client_name}{b.missing_rate && <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700"><AlertTriangle size={9} />est</span>}</td>
                          <td className="px-3 py-2 text-slate-500 text-xs">{b.fund_name || '—'}{b.support_item_number && <div className="text-[10px] text-slate-400 font-mono">{b.support_item_number}</div>}</td>
                          <td className="px-3 py-2 text-center text-slate-600">{fmtRatio(b.ratio_num, b.ratio_den)}</td>
                          <td className="px-3 py-2 text-center text-slate-600">{Number(b.hours).toFixed(1)}</td>
                          <td className="px-3 py-2 text-right text-slate-600">{money(b.rate)}</td>
                          <td className="px-3 py-2 text-right font-semibold text-slate-800">{money(b.line_total)}</td>
                          <td className="px-2 py-2"><button onClick={() => removeParticipant(b.client_id)} className="text-slate-400 hover:text-red-500"><Trash2 size={13} /></button></td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot><tr className="bg-cyan-50 text-cyan-800 font-semibold text-sm">
                      <td className="px-3 py-2" colSpan={5}>Total client charge ({billing.totals.count} participant{billing.totals.count === 1 ? '' : 's'}, 1 carer paid)</td>
                      <td className="px-3 py-2 text-right">{money(billing.totals.lineCharge)}</td><td />
                    </tr></tfoot>
                  </table>
                </div>
              )}
              {billing?.shift && (
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold">Charge {money(billing.shift.total_client_charge)}</span>
                  <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-600">Staff pay {money(billing.shift.total_staff_pay)}</span>
                  <span className="px-3 py-1 rounded-full bg-violet-50 text-violet-700 font-semibold">Profit {money(billing.shift.shift_profit)}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50">{step === 2 ? 'Done' : 'Cancel'}</button>
          {step === 1 && (
            <button onClick={createSkeleton} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl text-sm font-semibold disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Create & add participants
            </button>
          )}
          {step === 2 && (
            <button onClick={() => { onSaved && onSaved(); onClose(); }} className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl text-sm font-semibold">
              <Check size={14} /> Save & close
            </button>
          )}
        </div>

        {/* Select Clients drawer */}
        {drawer && (
          <div className="absolute inset-0 z-10 flex justify-end bg-black/20" onClick={() => setDrawer(false)}>
            <div className="bg-white w-full max-w-md h-full shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                <h4 className="font-semibold text-slate-800">Select Clients</h4>
                <button onClick={() => setDrawer(false)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400"><X size={16} /></button>
              </div>
              <div className="px-5 py-3 border-b border-slate-100">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={autoRatio} onChange={e => setAutoRatio(e.target.checked)} className="rounded accent-cyan-600" />
                  Auto-Ratio (1:{checkedCount || '?'} — every participant shares the group)
                </label>
              </div>
              <div className="overflow-y-auto flex-1 p-3 space-y-1">
                {loading && <div className="flex justify-center py-8"><Loader2 className="animate-spin text-cyan-500" /></div>}
                {!loading && eligible.map(c => {
                  const p = picked[c.id];
                  return (
                    <div key={c.id} className={`p-2.5 rounded-xl border ${p?.checked ? 'border-cyan-300 bg-cyan-50' : 'border-slate-200'}`}>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={!!p?.checked} onChange={() => togglePick(c)} className="rounded accent-cyan-600" />
                        <span className="text-sm font-medium text-slate-800 flex-1">{c.first_name} {c.last_name}</span>
                        {c.is_resident && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-100 text-cyan-700">resident</span>}
                        {!c.funded && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">no fund</span>}
                      </label>
                      {p?.checked && (
                        <div className="mt-2 ml-6 flex gap-2">
                          <select className="text-xs border border-slate-200 rounded-lg px-2 py-1 flex-1" value={p.ratioDen} disabled={autoRatio}
                            onChange={e => setPicked(pp => ({ ...pp, [c.id]: { ...pp[c.id], ratioDen: parseInt(e.target.value, 10) } }))}>
                            {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>1:{n}</option>)}
                          </select>
                          <span className="text-xs text-slate-400 self-center">{c.fund_name || 'no fund'}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
                {!loading && eligible.length === 0 && <p className="text-center text-slate-400 text-sm py-8">No clients found.</p>}
              </div>
              <div className="px-5 py-4 border-t border-slate-100 flex justify-end gap-2">
                <button onClick={() => setDrawer(false)} className="px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-600">Cancel</button>
                <button onClick={addSelected} disabled={saving || !checkedCount} className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl text-sm font-semibold disabled:opacity-60">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add {checkedCount || ''} Selected
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, hint }) {
  return <div><label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>{children}{hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}</div>;
}
